import React from 'react';
import { format, parse } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useIsMutating } from '@tanstack/react-query';

import Modal from '@hero/ui-kit/components/Modal';
import Button from '@hero/ui-kit/inputs/Button';
import P from '@hero/ui-kit/typography/P';

import Layout from '../../components/Layout';
import ListFilter from '../../components/ListFilter';
import Error from '../../components/States/Error';
import { useUserStatusContext } from '../../context/UserStatus';
import useCareMonth from './api/useCareMonth';
import useCareMonthRows from './api/useCareMonthRows';
import useCareScope from './api/useCareScope';
import { CARE_WRITE_KEY } from './api/constants';
import { useImportMinutes, usePopulateMonth, useQualifyMonth } from './api/useCareWrites';
import ActionForm from './components/ActionForm';
import BillableByCode from './components/BillableByCode';
import ChecklistPanel from './components/ChecklistPanel';
import EnrollmentPanel from './components/EnrollmentPanel';
import EvidencePanel from './components/EvidencePanel';
import HistoryPanel from './components/HistoryPanel';
import LockForm from './components/LockForm';
import MinutesImport from './components/MinutesImport';
import MonthList from './components/MonthList';
import ReadinessPanel from './components/ReadinessPanel';
import ReconciliationPanel from './components/ReconciliationPanel';
import RowsTable from './components/RowsTable';
import RunPanel from './components/RunPanel';
import StatusSummary from './components/StatusSummary';
import TerminationPanel from './components/TerminationPanel';
import { careErrorText, monthOptions, previousMonth } from './constants';
import * as Style from './style.module.scss';
import { ActionTarget } from './types';
import { actionKey, baseCodeFor, selectedEvidenceRow, resultContextsAgree } from './presentation';

// Sync first: a sync failure leaves later calculations based on an old population.
const WRITE_OPERATIONS = ['sync', 'calculate'] as const;
type WriteOperation = (typeof WRITE_OPERATIONS)[number];

const CareMonth: React.FC<{ month: string; months: string[] }> = ({ month, months }) => {
    const { permissions } = useUserStatusContext();
    const canWrite = permissions.iam.editInvites;
    const orgId = useCareScope();
    const writesPending = useIsMutating({ mutationKey: [CARE_WRITE_KEY, orgId, month] }) > 0;
    const [searchParams, setSearchParams] = useSearchParams();
    const [actionTarget, setActionTarget] = React.useState<ActionTarget>();
    const [showAction, setShowAction] = React.useState(false);
    const [showLock, setShowLock] = React.useState(false);
    const [showImport, setShowImport] = React.useState(false);
    const [detailChargeId, setDetailChargeId] = React.useState<number>();
    const [showEvidence, setShowEvidence] = React.useState(false);
    const [showHistory, setShowHistory] = React.useState(false);
    const [writeErrors, setWriteErrors] = React.useState<Partial<Record<WriteOperation, string>>>({});
    const {
        data: overview,
        error: overviewError,
        isFetching: isMonthFetching,
        refetch: refetchOverview
    } = useCareMonth(month);
    const {
        data: rows,
        error: rowsError,
        isFetching: isRowsFetching,
        refetch: refetchRows
    } = useCareMonthRows(month);
    const populate = usePopulateMonth(month);
    const qualify = useQualifyMonth(month);
    const minutesImport = useImportMinutes(month);
    // Failures are tracked per operation so each success clears only its own alert.
    const setWriteError = (operation: WriteOperation, message?: string) =>
        setWriteErrors((current) => ({ ...current, [operation]: message }));
    const calculate = () => qualify.mutate(undefined, {
        onSuccess: () => setWriteError('calculate'),
        onError: (error) => setWriteError('calculate', `Billing calculation failed. ${careErrorText(error)}`)
    });
    const sync = () => populate.mutate(undefined, {
        onSuccess: () => setWriteError('sync'),
        onError: (error) => setWriteError('sync', `Patient sync failed. ${careErrorText(error)}`)
    });
    const onCalculationResult = (message?: string) => setWriteError('calculate', message);
    const writable =
        canWrite &&
        overview !== undefined &&
        !overviewError &&
        !isMonthFetching &&
        !overview.run.lock.locked;
    const controlsDisabled =
        !writable || writesPending;
    const currentStatus = searchParams.get('current_status');
    const code = searchParams.get('code');
    const changedOnly = searchParams.get('changed') === 'true';
    const billableOnly = searchParams.get('billable_only') === 'true';
    const missingOnly = searchParams.get('missing') === 'true';
    const detailRow = selectedEvidenceRow(rows, overview, detailChargeId);
    const rowsById = new Map(rows?.rows.map((row) => [row.charge_id, row]));
    const historyRow = rows?.rows.find((row) => row.charge_id === detailChargeId);
    const changedHistoryRows = rows?.rows.filter(
        (row) => row.hero_user_id === historyRow?.hero_user_id && row.changes.changed
    ) ?? [];
    const resultsAgree = resultContextsAgree(rows, overview);
    const refreshResults = () => {
        refetchRows();
        refetchOverview();
    };
    const setStatus = (value: string | null) =>
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (value) next.set('current_status', value);
            else next.delete('current_status');
            ['status', 'code', 'billable_only', 'missing', 'changed'].forEach((key) =>
                next.delete(key)
            );
            return next;
        });
    const selectCode = (value: string) =>
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            ['status', 'current_status', 'changed', 'missing'].forEach((key) => next.delete(key));
            next.set('code', value);
            next.set('billable_only', 'true');
            return next;
        });
    const now = new Date();
    const toggleFilter = (key: 'changed' | 'missing', selected: boolean) =>
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (selected) next.delete(key);
            else next.set(key, 'true');
            if (key === 'missing') {
                ['status', 'current_status', 'code', 'billable_only'].forEach((value) =>
                    next.delete(value)
                );
            }
            return next;
        });
    const filters = (
        <>
            <ListFilter
                options={months.map((value) => ({
                    value,
                    label: format(parse(value, 'yyyy-MM', now), 'MMMM yyyy'),
                    dividerAfter: value.endsWith('-01') && value !== months[months.length - 1]
                }))}
                searchParam="month"
                label="Month"
                disabled={writesPending}
                mode="single"
                defaultValues={[previousMonth(now)]}
                formatLabel={() => `Month: ${format(parse(month, 'yyyy-MM', now), 'MMMM yyyy')}`}
                resetPaginationKeys={[
                    'status',
                    'current_status',
                    'code',
                    'billable_only',
                    'changed',
                    'missing'
                ]}
                renderContent={(options, selectedValues, onToggle) => (
                    <MonthList options={options} selected={selectedValues} onToggle={onToggle} />
                )}
            />
            <button
                type="button"
                role="switch"
                aria-checked={changedOnly}
                className={Style.filterSwitch}
                onClick={() => toggleFilter('changed', changedOnly)}
            >
                <span className={Style.switchTrack} aria-hidden="true" />
                <span>Changed this month</span>
            </button>
            <button
                type="button"
                role="switch"
                aria-checked={missingOnly}
                className={Style.filterSwitch}
                disabled={!missingOnly && !overview?.is_current}
                onClick={() => toggleFilter('missing', missingOnly)}
            >
                <span className={Style.switchTrack} aria-hidden="true" />
                <span>Missing from minutes CSV</span>
            </button>
            {code ? (
                <Button
                    size="small"
                    variant="outlineSecondary"
                    width="natural"
                    noDefaultMargin
                    onClick={() => setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.delete('code');
                        next.delete('billable_only');
                        return next;
                    })}
                >
                    Clear {code} filter
                </Button>
            ) : null}
        </>
    );
    const controls = (
        <>
            {canWrite ? (
                <>
                    <Button
                        size="small"
                        variant="outlineSecondary"
                        width="natural"
                        noDefaultMargin
                        disabled={controlsDisabled}
                        onClick={() => {
                            minutesImport.reset();
                            setShowImport(true);
                        }}
                    >
                        Import CSV
                    </Button>
                    <Button
                        size="small"
                        variant="outlineSecondary"
                        width="natural"
                        noDefaultMargin
                        disabled={controlsDisabled}
                        onClick={sync}
                    >
                        Sync patients
                    </Button>
                    <Button
                        size="small"
                        variant="outlineSecondary"
                        width="natural"
                        noDefaultMargin
                        disabled={controlsDisabled}
                        onClick={calculate}
                    >
                        Calculate billable codes
                    </Button>
                    <Button
                        size="small"
                        variant="primary"
                        width="natural"
                        noDefaultMargin
                        disabled={controlsDisabled}
                        onClick={() => setShowLock(true)}
                    >
                        Lock month
                    </Button>
                </>
            ) : null}
        </>
    );
    return (
        <Layout>
            <div className={Style.sections}>
                <StatusSummary
                    counts={overview?.current_status_counts}
                    filter={currentStatus}
                    onFilter={setStatus}
                    filters={filters}
                    controls={controls}
                />
                {WRITE_OPERATIONS.map((operation) =>
                    writeErrors[operation] ? (
                        <div key={operation} role="alert" className={Style.writeAlert}>
                            <P noDefaultMargin>{writeErrors[operation]}</P>
                            <Button
                                size="small"
                                variant="outlineSecondary"
                                width="natural"
                                noDefaultMargin
                                disabled={controlsDisabled}
                                onClick={operation === 'sync' ? sync : calculate}
                            >
                                {operation === 'sync' ? 'Sync patients' : 'Calculate billable codes'}
                            </Button>
                        </div>
                    ) : null
                )}
                {overviewError ? (
                    <Error errorMessage={careErrorText(overviewError)} />
                ) : (
                    <>
                        {overview?.run.lock.locked ? <P strong>Locked month · Read only</P> : null}
                        {rowsError ? (
                            <P className={Style.apiError} noDefaultMargin>
                                {careErrorText(rowsError)}
                            </P>
                        ) : (
                            <RowsTable
                                month={month}
                                organizationId={orgId}
                                downloadDisabledReason={writesPending ? 'Wait for patient changes to finish.'
                                    : isRowsFetching ? 'Patient rows are loading.' : undefined}
                                rows={rows}
                                filter={{
                                    currentStatus,
                                    code,
                                    billableOnly,
                                    changedOnly,
                                    missingOnly
                                }}
                                canWrite={canWrite && overview !== undefined && !overview.run.lock.locked}
                                actionsDisabled={
                                    !writable || writesPending || isRowsFetching || !resultsAgree
                                }
                                onAction={(target) => {
                                    setActionTarget(target);
                                    setShowAction(true);
                                }}
                                onEvidence={(row) => {
                                    setDetailChargeId(row.charge_id);
                                    setShowEvidence(true);
                                }}
                                onHistory={(row) => {
                                    setDetailChargeId(row.charge_id);
                                    setShowHistory(true);
                                }}
                            />
                        )}
                        {rows &&
                        overview &&
                        !isRowsFetching &&
                        !isMonthFetching &&
                        !resultsAgree ? (
                            <P>
                                Results changed between reads.{' '}
                                <Button
                                    size="small"
                                    variant="outlineSecondary"
                                    onClick={refreshResults}
                                >
                                    Refresh month results
                                </Button>
                            </P>
                        ) : null}
                        <div className={Style.details}>
                            <ChecklistPanel checklist={overview?.current_checklist} />
                            <RunPanel
                                run={overview?.run}
                                ruleVersion={overview?.rule_version}
                                checklist={overview?.current_checklist}
                            />
                            <ReconciliationPanel
                                reconciliation={overview?.current_reconciliation}
                            />
                            <BillableByCode
                                report={overview?.current_report}
                                onCode={selectCode}
                                onReview={() => setStatus('Needs review')}
                            />
                        </div>
                        <details className={Style.accordion}>
                            <summary className={Style.accordionSummary}>
                                Additional month details
                            </summary>
                            <div className={Style.additionalDetails}>
                                <ReadinessPanel month={month} />
                                <EnrollmentPanel enrollment={overview?.enrollment} />
                                <TerminationPanel rows={rows} />
                            </div>
                        </details>
                    </>
                )}
            </div>
            <Modal
                externalControls={[showImport, setShowImport]}
                className={Style.careModal}
                noDefaultPadding
                isCancelable={!minutesImport.isPending}
            >
                {showImport ? (
                    <MinutesImport
                        month={month}
                        disabled={controlsDisabled}
                        mutation={minutesImport}
                    />
                ) : null}
            </Modal>
            <Modal
                externalControls={[showAction, setShowAction]}
                className={Style.careModal}
                noDefaultPadding
                isCancelable
            >
                <div className={Style.formModal}>
                    {showAction && actionTarget ? (
                        <ActionForm
                            key={`${actionTarget.row.charge_id}:${actionKey(actionTarget.action)}`}
                            month={month}
                            target={actionTarget}
                            onCalculationResult={onCalculationResult}
                            baseCode={baseCodeFor(actionTarget.row, rowsById)}
                            dependentCodes={
                                rows?.rows
                                    .filter(
                                        (row) =>
                                            row.current_evidence.facts?.dependency?.base_charge_id ===
                                            actionTarget.row.charge_id
                                    )
                                    .map((row) => row.code) ?? []
                            }
                            canWrite={writable}
                            closeModal={() => setShowAction(false)}
                        />
                    ) : null}
                </div>
            </Modal>
            <Modal
                externalControls={[showLock, setShowLock]}
                className={Style.careModal}
                noDefaultPadding
                isCancelable
            >
                <div className={Style.formModal}>
                    {showLock ? (
                        <LockForm
                            month={month}
                            canWrite={writable}
                            closeModal={() => setShowLock(false)}
                        />
                    ) : null}
                </div>
            </Modal>
            <Modal
                externalControls={[showEvidence, setShowEvidence]}
                width="page"
                className={Style.careModal}
                noDefaultPadding
                isCancelable
            >
                {showEvidence &&
                !isMonthFetching &&
                !isRowsFetching &&
                !rowsError &&
                !overviewError &&
                detailRow ? (
                    <EvidencePanel row={detailRow} month={month} overview={overview}
                        baseCode={baseCodeFor(detailRow, rowsById)} />
                ) : showEvidence ? (
                    <div className={Style.drawerContent}>
                        <P>
                            Refreshing evidence. Current row and run context must agree before proof
                            can be displayed.
                        </P>
                        <Button
                            variant="outlineSecondary"
                            disabled={isMonthFetching || isRowsFetching}
                            onClick={refreshResults}
                        >
                            Refresh evidence
                        </Button>
                    </div>
                ) : null}
            </Modal>
            <Modal
                externalControls={[showHistory, setShowHistory]}
                width="page"
                className={Style.careModal}
                noDefaultPadding
                isCancelable
            >
                {showHistory && historyRow ? (
                    <>
                        {changedHistoryRows.length > 1 ? (
                            <div className={Style.drawerContent}>
                                <P noDefaultMargin>Select a changed code to view its history.</P>
                                <div className={Style.dialogActions}>
                                    {changedHistoryRows.map((row) => (
                                        <Button
                                            key={row.charge_id}
                                            size="small"
                                            width="natural"
                                            noDefaultMargin
                                            variant={row.charge_id === historyRow.charge_id ? 'primary' : 'outlineSecondary'}
                                            aria-pressed={row.charge_id === historyRow.charge_id}
                                            onClick={() => setDetailChargeId(row.charge_id)}
                                        >
                                            {row.program} {row.code}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                        <HistoryPanel key={historyRow.charge_id} row={historyRow} month={month} />
                    </>
                ) : null}
            </Modal>
        </Layout>
    );
};

const MonthlyCare: React.FC = () => {
    const orgId = useCareScope();
    const [searchParams] = useSearchParams();
    const now = React.useMemo(() => new Date(), []);
    const months = React.useMemo(() => monthOptions(now), [now]);
    const requested = searchParams.get('month');
    const month = requested && months.includes(requested) ? requested : previousMonth(now);
    // Close all tenant/month-bound forms and uploads before a new scope can render.
    return <CareMonth key={`${orgId}:${month}`} month={month} months={months} />;
};

export default MonthlyCare;
